import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { DEFAULT_MODEL_CONFIG } from '@modelstack/shared-schema/auth'
import type { ProviderTier } from '../../../shared-pricing-config'
import { ModelList } from '@/features/models/components'
import { useModelFilters } from '@/features/models/hooks'
import { modelCatalog } from '@/features/models/services/modelCatalogService'
import { useUserTierPreferences } from '@/features/user-settings/hooks/useUserTierPreferences'
import {
  useModelCatalog,
  useModelUptimeMap,
} from '@/shared/hooks/useModelCatalog'
import { copyModelId } from '@/features/models/utils/modelsFormatters'
import { isGenerationModel } from '@/features/models/types/unified-model.types'
import { ModelsCatalogHeader } from '@/features/models/components/ModelsCatalogHeader'
import { ModelsSidebar } from '@/features/models/components/ModelsSidebar'
import { ModelTypeTabs } from '@/features/models/components/ModelTypeTabs'
import { useSession } from '@/server/infrastructure/auth/client'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'
import {
  getBreadcrumbSchema,
  getModelsItemListSchema,
  renderJsonLd,
} from '@/lib/structured-data'

export const Route = createFileRoute('/models/')({
  head: () => ({
    meta: generateMetaTags(routeSEO['/models']),
    links: generateLinks(routeSEO['/models']),
  }),
  component: ModelsPage,
})

function ModelsPage() {
  const { data: session } = useSession()
  const { data: tierPreferences } = useUserTierPreferences(!!session?.user)
  const enabledTiers: Array<ProviderTier> =
    tierPreferences?.modelConfig.default.tiers ??
    DEFAULT_MODEL_CONFIG.default.tiers

  const catalog = useModelCatalog()
  const uptimeByModel = useModelUptimeMap()

  const unifiedModels = useMemo(() => {
    const allModels = modelCatalog.getAllModels()
    return allModels.map((model) => ({
      ...model,
      availableTiers: catalog.getAvailableTiers(model.id),
      uptimeHistory: uptimeByModel.get(model.id)?.segments,
    }))
  }, [catalog, uptimeByModel])

  const { filters, handlers, filteredModels, modelFamilies } =
    useModelFilters(unifiedModels)

  const counts = useMemo(
    () => ({
      chat: unifiedModels.filter((m) => m.type === 'chat').length,
      media: unifiedModels.filter((m) => m.type === 'generation').length,
      image: unifiedModels.filter(
        (m) => isGenerationModel(m) && m.metadata.generationType === 'image',
      ).length,
      video: unifiedModels.filter(
        (m) => isGenerationModel(m) && m.metadata.generationType === 'video',
      ).length,
      audio: unifiedModels.filter(
        (m) => isGenerationModel(m) && m.metadata.generationType === 'audio',
      ).length,
      providers: new Set(unifiedModels.map((m) => m.family)).size,
    }),
    [unifiedModels],
  )

  // Mirrors what the page actually renders, so the ItemList never claims
  // models that are filtered out of view.
  const itemListJsonLd = useMemo(
    () =>
      renderJsonLd(
        getModelsItemListSchema(
          filteredModels.map(({ id, name, provider }) => ({
            id,
            name,
            provider,
          })),
        ),
      ),
    [filteredModels],
  )

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-clip bg-[var(--color-bg-primary)]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: renderJsonLd(
            getBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Models', url: '/models' },
            ]),
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: itemListJsonLd }}
      />

      <Header maxWidthClassName="max-w-[1600px]" />
      <main className="mx-auto w-full max-w-[1600px] flex-1 px-6 pt-28 pb-20">
        <ModelsCatalogHeader
          chatCount={counts.chat}
          mediaCount={counts.media}
          providerCount={counts.providers}
        />
        <div className="mt-10">
          <ModelTypeTabs
            value={filters.selectedModelType}
            onChange={handlers.setSelectedModelType}
            counts={{
              All: unifiedModels.length,
              chat: counts.chat,
              image: counts.image,
              video: counts.video,
              audio: counts.audio,
            }}
          />
        </div>
        <div className="mt-8 flex flex-col gap-8 lg:flex-row">
          <ModelsSidebar
            filters={filters}
            handlers={handlers}
            modelFamilies={modelFamilies}
          />
          <ModelList
            models={filteredModels}
            totalCount={unifiedModels.length}
            enabledTiers={enabledTiers}
            loading={false}
            onCopy={copyModelId}
          />
        </div>
      </main>
      <Footer />
    </div>
  )
}
